import env from 'config/env'
import StateStorage from 'lib/state-storage'
import { GTM_CONTAINER_ID } from 'pages/const'

// Paid purchases go to the site container, which _document loads only when
// the PDPA cookie allows marketing. This module waits for it; it never loads
// a container itself.
const CONTAINER_ID = GTM_CONTAINER_ID
const LAYER_NAME = 'dataLayer'
const CONTAINER_POLL_MS = 250
const CONTAINER_WAIT_MS = 10000
const listeners = new Set()
export const GOOGLE_ADS_CANDIDATES_KEY =
  'pomelo-google-ads-purchase-candidates-v1'

let loadState = 'idle'
let initialized = false
let lastConsent
let lastState
let consentCleanupFailed = false

const isProductionSite = () =>
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  env.NODE_ENV === 'production' &&
  window.location.protocol === 'https:' &&
  window.location.hostname === 'www.pomelofashion.com'

const readConsent = () => {
  const denied = { marketing: false, performance: false }
  if (!isProductionSite()) return denied

  try {
    const cookie = StateStorage.getCookie('bannerPDPACookieConsent')
    const consent = typeof cookie === 'string' ? JSON.parse(cookie) : cookie
    return {
      marketing: consent?.is_marketing_allowed === true,
      performance: consent?.is_performance_allowed === true,
    }
  } catch (error) {
    return denied
  }
}

export const hasGoogleAdsMarketingConsent = () =>
  !consentCleanupFailed && readConsent().marketing

const clearUnsentCandidates = () => {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(GOOGLE_ADS_CANDIDATES_KEY)
      if (window.sessionStorage.getItem(GOOGLE_ADS_CANDIDATES_KEY) !== null) {
        throw new Error('Unsent purchase cleanup was not persisted')
      }
    }
    consentCleanupFailed = false
  } catch (error) {
    // Do not recover old attribution candidates after a withdrawal we could
    // not persist. Retry cleanup before allowing a later grant to emit.
    consentCleanupFailed = true
  }
}

const getState = () => ({
  consent: hasGoogleAdsMarketingConsent(),
  ready: loadState === 'loaded',
})

const notifyListener = (listener, state) => {
  try {
    listener(state)
  } catch (error) {
    // Tracking observers must not interrupt checkout or other observers.
  }
}

const notifyState = () => {
  const state = getState()
  if (
    lastState?.consent === state.consent &&
    lastState?.ready === state.ready
  ) {
    return
  }
  lastState = state
  listeners.forEach((listener) => notifyListener(listener, state))
}

export const subscribeGoogleAdsState = (listener) => {
  if (typeof listener !== 'function') return () => {}
  listeners.add(listener)
  // Read the real cookie; an invented initial denial could erase a return candidate.
  notifyListener(listener, getState())
  return () => listeners.delete(listener)
}

const updateConsent = (consent) => {
  // Lets GTM tags react to consent changes during the page; the container's
  // Consent Mode tag reads the actual cookie. These values never contain PII.
  window[LAYER_NAME].push({
    event: 'pomelo_ads_consent',
    pomelo_ads_consent: { ...consent },
  })
  if (consent.marketing) {
    // A separate event lets GTM's consent API update complete before tags run.
    // Base/linker tags use this plus Initialization with once-per-load firing,
    // so regrant can recover a first load that finished during withdrawal.
    window[LAYER_NAME].push({ event: 'pomelo_ads_ready' })
  }
  lastConsent = consent
}

const containerIsAvailable = () => !!window.google_tag_manager?.[CONTAINER_ID]

const markContainerReady = () => {
  loadState = 'loaded'
  // Recheck withdrawal during the wait before observers can emit anything.
  try {
    const currentConsent = readConsent()
    if (!currentConsent.marketing) {
      clearUnsentCandidates()
      updateConsent(currentConsent)
    }
  } catch (error) {
    loadState = 'failed'
  }
  notifyState()
}

const waitForContainer = (consent) => {
  if (loadState === 'loaded' || loadState === 'waiting') return

  if (!initialized) {
    // Google supports one data layer per page. Keep the legacy queue and use
    // unique events/fields; the container's own snippet pushes its loader event.
    if (window[LAYER_NAME] === undefined) window[LAYER_NAME] = []
    if (!Array.isArray(window[LAYER_NAME])) return
    updateConsent(consent)
    initialized = true
  }

  if (containerIsAvailable()) {
    markContainerReady()
    return
  }

  loadState = 'waiting'
  let polls = 0
  const poll = () => {
    if (containerIsAvailable()) {
      markContainerReady()
      return
    }
    polls += 1
    if (polls * CONTAINER_POLL_MS >= CONTAINER_WAIT_MS) {
      // Not on this page: consent was granted after the HTML was rendered, or
      // GTM is blocked. A later sync starts a new wait.
      loadState = 'failed'
      notifyState()
      return
    }
    setTimeout(poll, CONTAINER_POLL_MS)
  }
  setTimeout(poll, CONTAINER_POLL_MS)
}

export const syncGoogleAdsConsent = () => {
  try {
    const consent = readConsent()
    if (!consent.marketing || consentCleanupFailed) clearUnsentCandidates()
    if (
      initialized &&
      (lastConsent?.marketing !== consent.marketing ||
        lastConsent?.performance !== consent.performance)
    ) {
      updateConsent(consent)
    }
    if (consent.marketing && !consentCleanupFailed) waitForContainer(consent)
    notifyState()
    return consent.marketing && !consentCleanupFailed && loadState === 'loaded'
  } catch (error) {
    loadState = 'failed'
    notifyState()
    return false
  }
}

export const emitGoogleAdsPurchase = (purchase) => {
  if (
    !purchase ||
    typeof purchase.transaction_id !== 'string' ||
    !/^[1-9]\d*$/.test(purchase.transaction_id) ||
    typeof purchase.value !== 'number' ||
    !Number.isFinite(purchase.value) ||
    purchase.value <= 0 ||
    typeof purchase.currency !== 'string' ||
    !/^[A-Z]{3}$/.test(purchase.currency)
  ) {
    return false
  }

  if (!syncGoogleAdsConsent() || !hasGoogleAdsMarketingConsent()) return false

  // A push failure is ambiguous: the caller protects checkout and retains its
  // reserved transaction ID rather than potentially sending the same order twice.
  window[LAYER_NAME].push({
    event: 'pomelo_paid_purchase',
    pomelo_ads_purchase: {
      transaction_id: purchase.transaction_id,
      value: purchase.value,
      currency: purchase.currency,
    },
  })
  return true
}
